/**
 * Authentication Context
 * Module 07 - Dashboard UI
 * Section 03 - Step 3.3
 * 
 * Provides authentication state and methods throughout the application
 */

import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import type { AxiosError } from 'axios';
import { User, AuthContextType, LoginCredentials, RegisterCredentials } from '../types/auth.types';
import { authService } from '../services/auth.service';
import { setUnauthorizedHandler } from '../services/api';
import { useToast } from '../hooks/useToast';

export const AuthContext = createContext<AuthContextType | undefined>(undefined);
export type { AuthContextType };

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { showToast } = useToast();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const expiryTimeoutRef = useRef<number | null>(null);
  const storage =
    import.meta.env.VITE_AUTH_PERSISTENCE === 'local'
      ? localStorage
      : sessionStorage;
  const useCookieAuth = import.meta.env.VITE_AUTH_USE_COOKIES === 'true';

  /**
   * Handle user logout
   * Clears token and user data from storage and state
   */
  const handleLogout = useCallback(({ silent = false }: { silent?: boolean } = {}) => {
    if (expiryTimeoutRef.current) {
      window.clearTimeout(expiryTimeoutRef.current);
      expiryTimeoutRef.current = null;
    }
    // Clear storage
    if (!useCookieAuth) {
      storage.removeItem('token');
    }
    storage.removeItem('user');
    
    // Clear state
    setToken(null);
    setUser(null);
    
    // Call logout endpoint (fire and forget)
    authService.logout().catch(() => {
      // Ignore logout errors - user is logged out locally anyway
    });
    
    if (!silent) {
      showToast('info', 'Logged out successfully');
    }
  }, [showToast, storage, useCookieAuth]);

  const scheduleExpiryLogout = useCallback((authToken: string) => {
    if (!authToken || useCookieAuth) return;
    // Clear any previous timer
    if (expiryTimeoutRef.current) {
      window.clearTimeout(expiryTimeoutRef.current);
      expiryTimeoutRef.current = null;
    }

    try {
      const [, payload] = authToken.split('.');
      const decoded = JSON.parse(atob(payload || ''));
      const expMs = decoded?.exp ? decoded.exp * 1000 : null;
      if (expMs && expMs > Date.now()) {
        const timeoutMs = Math.max(expMs - Date.now() - 5000, 0); // logout 5s before expiry
        expiryTimeoutRef.current = window.setTimeout(() => {
          handleLogout({ silent: true });
          showToast('info', 'Session expired, please log in again.');
        }, timeoutMs);
      }
    } catch {
      // If token is not JWT or decode fails, skip scheduling
    }
  }, [handleLogout, showToast, useCookieAuth]);

  /**
   * Load authentication state from storage on mount
   */
  useEffect(() => {
    setUnauthorizedHandler(() => {
      handleAuthError();
    });
    
    // Clear legacy localStorage tokens if switching to session-based storage
    if (storage !== localStorage) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }

    const storedToken = useCookieAuth ? null : storage.getItem('token');
    const storedUser = storage.getItem('user');

    if (useCookieAuth) {
      // With cookie auth, attempt to load profile directly
      authService
        .getProfile()
        .then((profile) => {
          setUser(profile);
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
        });
      return;
    }

    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
      scheduleExpiryLogout(storedToken);
      
      // Validate token with backend
      authService.validateToken()
        .then((isValid) => {
          if (!isValid) {
            handleLogout({ silent: true });
          }
          setIsLoading(false);
        })
        .catch(() => {
          handleLogout({ silent: true });
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, []);

  /**
   * Handle user login
   * Stores token and user data in storage and state
   */
  const handleLogin = useCallback(async (credentials: LoginCredentials) => {
    try {
      const { token, user } = await authService.login(credentials);
      
      // Store in storage (session by default, local if configured)
      if (!useCookieAuth) {
        storage.setItem('token', token);
      }
      storage.setItem('user', JSON.stringify(user));
      
      // Update state
      setToken(useCookieAuth ? null : token);
      setUser(user);
      scheduleExpiryLogout(token);
      
      showToast('success', 'Login successful');
    } catch (error: unknown) {
      const errorMessage = (error as AxiosError<{ message?: string }>).response?.data?.message || 'Login failed';
      showToast('error', errorMessage);
      throw error;
    }
  }, [scheduleExpiryLogout, showToast]);

  /**
   * Handle user registration
   * Automatically logs in the user after successful registration
   */
  const handleRegister = useCallback(async (credentials: RegisterCredentials) => {
    try {
      const { token, user } = await authService.register(credentials);
      
      // Store in storage (session by default, local if configured)
      if (!useCookieAuth) {
        storage.setItem('token', token);
      }
      storage.setItem('user', JSON.stringify(user));
      
      // Update state
      setToken(useCookieAuth ? null : token);
      setUser(user);
      scheduleExpiryLogout(token);
      
      showToast('success', 'Account created successfully');
    } catch (error: unknown) {
      const errorMessage = (error as AxiosError<{ message?: string }>).response?.data?.message || 'Registration failed';
      showToast('error', errorMessage);
      throw error;
    }
  }, [scheduleExpiryLogout, showToast]);

  /**
   * Re-fetch the user profile and update auth state.
   * Call this after mutations that affect user quota (model delete/upload).
   */
  const refreshUser = useCallback(async () => {
    try {
      const freshUser = await authService.getProfile();
      storage.setItem('user', JSON.stringify(freshUser));
      setUser(freshUser);
    } catch {
      // Silently ignore — stale data is preferable to logging the user out
    }
  }, [storage]);

  /**
   * Attempt refresh/validation flow before hard logout on 401
   */
  const handleAuthError = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      const valid = await authService.validateToken();
      if (!valid) {
        handleLogout({ silent: true });
      }
    } catch {
      handleLogout({ silent: true });
    } finally {
      setIsRefreshing(false);
    }
  }, [handleLogout, isRefreshing]);

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!user && !!token,
    isLoading,
    login: handleLogin,
    register: handleRegister,
    logout: handleLogout,
    handleAuthError,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
